import { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { loader } from 'graphql.macro';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styles from './WorkspaceAdminStrip.module.scss';
import SimpleUserCard from '../../components/SimpleUserCard';
import { Text } from '../../components/typography';
import Icon from '../../components/Icon';
import UniversalDropdown from '../../components/dropdownV2/UniversalDropdown';
import { keys } from '../../utilities/translator/translation_keys';
import { defaultWorkspaceLogo } from '../../utilities';
import { getRoute } from '../../constants/strings';
import { useCurrentUser } from '../../providers/UserProvider';
import { useModal } from '../../providers/ModalProvider';
import HighlightText from './HighlightText';

const workspaceAdminStripQuery = loader('./WorkspaceAdminStrip.graphql');
const addWorkspaceAdminMutation = loader(
  './WorkspaceAdminStrip.addUser.graphql',
);
const removeWorkspaceAdminMutation = loader(
  './WorkspaceAdminStrip.removeUser.graphql',
);

export default function WorkspaceAdminStrip({ searchQuery = '' }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user: currentUser, isAdmin } = useCurrentUser();
  const { openConfirmationModal } = useModal();
  const [editingWorkspaceId, setEditingWorkspaceId] = useState(null);

  const {
    data: { workspaces = [], workspacePermissions = [], users = [] } = {},
    refetch,
  } = useQuery(workspaceAdminStripQuery);

  const [addWorkspaceAdmin] = useMutation(addWorkspaceAdminMutation);
  const [removeWorkspaceAdmin] = useMutation(removeWorkspaceAdminMutation);

  const adminsByWorkspace = useMemo(() => {
    const map = {};
    for (const permission of workspacePermissions) {
      if (permission.accessLevel !== 'ADMIN') continue;
      if (!map[permission.workspaceId]) map[permission.workspaceId] = [];
      if (permission.user) map[permission.workspaceId].push(permission.user);
    }
    return map;
  }, [workspacePermissions]);

  // Matches either the workspace's own name or one of its admins' names, so
  const visibleWorkspaces = useMemo(() => {
    const query = searchQuery?.trim().toLowerCase();
    if (!query) return workspaces;
    return workspaces.filter((workspace) => {
      if (workspace.title?.toLowerCase().includes(query)) return true;
      return (adminsByWorkspace[workspace.id] || []).some((admin) =>
        `${admin.firstName} ${admin.lastName}`.toLowerCase().includes(query),
      );
    });
  }, [workspaces, adminsByWorkspace, searchQuery]);

  const canEditWorkspace = (workspaceId) =>
    isAdmin ||
    workspacePermissions.some(
      (p) =>
        p.workspaceId === workspaceId &&
        p.accessLevel === 'ADMIN' &&
        p.user?.id === currentUser.id,
    );

  const addableUsers = (workspaceId) => {
    const adminIds = (adminsByWorkspace[workspaceId] || []).map((u) => u.id);
    return users.filter(
      (u) => !adminIds.includes(u.id) && u.active !== false && !u.isSupport,
    );
  };

  const handleAddAdmin = async (workspace, selectedUser) => {
    if (!selectedUser) return;
    const existingMembership = workspacePermissions.find(
      (p) => p.workspaceId === workspace.id && p.user?.id === selectedUser.id,
    );
    if (existingMembership) {
      await removeWorkspaceAdmin({
        variables: { id: workspace.id, userId: selectedUser.id },
      });
    }
    await addWorkspaceAdmin({
      variables: {
        id: workspace.id,
        userId: selectedUser.id,
        accessLevel: 'ADMIN',
      },
    });
    refetch();
  };

  const handleRemoveAdmin = (workspace, admin) => {
    openConfirmationModal({
      variant: 'danger',
      icon: 'person_remove',
      title: `Remove ${admin.firstName} ${admin.lastName} as admin?`,
      description: `This removes them from ${workspace.title} entirely, not just their admin status — they'd need to be re-added to rejoin the workspace at all.`,
      buttonText: 'Remove',
      onSubmit: async () => {
        await removeWorkspaceAdmin({
          variables: { id: workspace.id, userId: admin.id },
        });
        refetch();
      },
    });
  };

  if (!workspaces.length) return null;

  if (!visibleWorkspaces.length) {
    return (
      <Text size="sm" color="secondary" noMargin>
        {t(keys.common.NONE)}
      </Text>
    );
  }

  return (
    <div className={styles.strip}>
      {visibleWorkspaces.map((workspace) => {
        const admins = adminsByWorkspace[workspace.id] || [];
        const isEditing = editingWorkspaceId === workspace.id;
        return (
          <div className={styles.card} key={workspace.id}>
            <div className={styles.cardHeader}>
              <div
                className={styles.titleLink}
                role="button"
                tabIndex={0}
                onClick={() =>
                  navigate(getRoute(workspace.id, 'workspaceSettings'))
                }
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    navigate(getRoute(workspace.id, 'workspaceSettings'));
                  }
                }}
              >
                <img
                  alt="workspace logo"
                  src={workspace.logoUrl || defaultWorkspaceLogo}
                  className={styles.logo}
                />
                <Text
                  weight="semiBold"
                  noMargin
                  truncate
                  className={styles.title}
                >
                  <HighlightText text={workspace.title} query={searchQuery} />
                </Text>
              </div>
              {canEditWorkspace(workspace.id) && (
                <Icon
                  size="sm"
                  className={styles.editIcon}
                  onClick={() =>
                    setEditingWorkspaceId(isEditing ? null : workspace.id)
                  }
                >
                  {isEditing ? 'check' : 'edit'}
                </Icon>
              )}
            </div>
            <div className={styles.adminList}>
              {admins.length ? (
                admins.map((admin) => (
                  <div className={styles.adminRow} key={admin.id}>
                    <SimpleUserCard
                      user={admin}
                      size="sm"
                      smallAvatar
                      wordsToHighlight={
                        searchQuery?.trim()
                          ? [searchQuery.trim().toLowerCase()]
                          : []
                      }
                    />
                    {isEditing && (
                      <Icon
                        size="xs"
                        className={styles.removeIcon}
                        onClick={() => handleRemoveAdmin(workspace, admin)}
                      >
                        close
                      </Icon>
                    )}
                  </div>
                ))
              ) : (
                <Text size="sm" color="secondary" noMargin>
                  {t(keys.common.NONE)}
                </Text>
              )}
            </div>
            {isEditing && (
              <div className={styles.addAdmin}>
                <UniversalDropdown
                  type="user"
                  size="sm"
                  placeholder="Add an admin..."
                  options={addableUsers(workspace.id)}
                  onChange={(selectedUser) =>
                    handleAddAdmin(workspace, selectedUser)
                  }
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
